import * as fs from 'node:fs';
import * as path from 'node:path';
import { getBridgeRoot } from '../../files/cwd/index.js';
import { yieldToEventLoop } from '../../runtime/yield-to-event-loop.js';
import type { IsolatedWorktreeBinding } from '../isolated-worktree-binding.js';
import type { WorktreeLayoutFile } from '../worktree-layout-file.js';
import { getLauncherDirNameIfPresent } from '../worktree-layout-file.js';
import { layoutDirSegments } from '../worktree-layout-kind.js';
import type { DiskWalkState } from './disk-walk-constants.js';
import { tryBindingFromIsolatedDirectory } from './try-binding-from-isolated-directory.js';
import { yieldDuringDiskWalk } from './disk-walk-utils.js';
import { layoutKeysForBridgeRoot } from './layout-keys-for-bridge-root.js';

export async function discoverPreviewWorktreeOnDisk(options: {
  environmentId: string;
  worktreesRootPath: string;
  layout: WorktreeLayoutFile;
  bridgeRoot: string;
}): Promise<IsolatedWorktreeBinding | null> {
  const { environmentId, worktreesRootPath, layout, bridgeRoot } = options;
  const eid = environmentId.trim();
  if (!eid) return null;
  try {
    await fs.promises.access(worktreesRootPath);
  } catch {
    return null;
  }

  const preferredKey = getLauncherDirNameIfPresent(layout, bridgeRoot);
  const keysOnDisk: string[] = [];
  if (preferredKey) keysOnDisk.push(preferredKey);
  const scanState: DiskWalkState = { entries: 0 };
  try {
    for (const name of await fs.promises.readdir(worktreesRootPath)) {
      if (!(await yieldDuringDiskWalk(scanState))) break;
      if (name.startsWith('.')) continue;
      const p = path.join(worktreesRootPath, name);
      let st: fs.Stats;
      try {
        st = await fs.promises.stat(p);
      } catch {
        continue;
      }
      if (!st.isDirectory()) continue;
      if (!keysOnDisk.includes(name)) keysOnDisk.push(name);
    }
  } catch {
    return null;
  }

  const keys = layoutKeysForBridgeRoot(layout, bridgeRoot, keysOnDisk);
  const previewDirRel = layoutDirSegments('preview_environment', eid);

  for (const key of keys) {
    await yieldToEventLoop();
    const previewDir = path.join(worktreesRootPath, key, ...previewDirRel);
    const binding = await tryBindingFromIsolatedDirectory(previewDir);
    if (binding) return binding;
  }
  return null;
}

export async function discoverPreviewWorktreeForBridge(options: {
  environmentId: string;
  worktreesRootPath: string;
  layout: WorktreeLayoutFile;
}): Promise<IsolatedWorktreeBinding | null> {
  return discoverPreviewWorktreeOnDisk({
    ...options,
    bridgeRoot: getBridgeRoot(),
  });
}
